import axios from 'axios';
import { BASE_URL } from '../api/config';

// Service du menu « Pièces jointes » : normes de pièces jointes par visa (API /normes-pj du backend admin).
// Pas de try/catch ici : l'erreur remonte au composant → toast.
// Les fonctions pures (résumé, validation) reprennent normesPj/schema.js du backend, qui reste l'autorité :
// mêmes textes, mêmes limites (testées par normesPjServices.test.ts).

export type FormatSortie = 'jpg' | 'png' | 'pdf';
export type Proportions = 'libres' | 'carree' | 'personnalisees';
export type CleFixe = 'photo' | 'passeport' | 'autres';

export interface ReglePiece {
  cle: string;
  libelle: string;
  formats: FormatSortie[];
  format_sortie: FormatSortie;
  taille_min_ko: number | null;
  taille_max_ko: number | null;
  largeur_min: number | null;
  largeur_max: number | null;
  hauteur_min: number | null;
  hauteur_max: number | null;
  proportions: Proportions;
  ratio_l: number | null;
  ratio_h: number | null;
}

export interface NormePj {
  id: number;
  libelle: string;
  visas_titres: string[];
  motif_tous: string;
  motif_un_parmi: string;
  pieces: ReglePiece[];
  note: string;
  actif: number;
  date_creation?: string;
  date_maj?: string | null;
  // Titres rattachés qui ne sont plus au catalogue (visa renommé dans Visas)
  titres_absents: string[];
}

export interface NormePjInput {
  libelle: string;
  visas_titres: string[];
  motif_tous: string;
  motif_un_parmi: string;
  pieces: ReglePiece[];
  note: string;
  actif: boolean;
}

export interface EtatOutils {
  sharp: string | null;
  pdf_lib: boolean;
  heic_convert: boolean;
  pdftoppm: string | null;
  pdfinfo: string | null;
}

export interface EtatNormesPj {
  tables_ok: boolean;
  raison: string | null;
  outils: EtatOutils;
}

export interface ChampObserve {
  cle: string;
  nb: number;
}

// ---------------------------------------------------------------------------
// Fenêtre « Normes PJ » d'une demande (GET /normes-pj/dossiers/:chemin)
// ---------------------------------------------------------------------------
export type OriginePiece = 'voyageur' | 'demande';
// manuel = norme imposée dans la fenêtre ; visa = titre du catalogue ; mots_cles = dossier hors catalogue
export type OrigineNorme = 'manuel' | 'visa' | 'mots_cles' | 'aucune';

// ok : true conforme, false non conforme, null hors norme (aucune règle ne s'applique)
export interface Conformite {
  ok: boolean | null;
  raisons: string[];
}

// Paramètres d'une conversion : page d'un PDF converti en image (null = 1), cadre ajusté à la main (null = automatique)
export interface ParametresConversion {
  page: number | null;
  recadrage: RectNormalise | null;
}

export interface ConversionPiece {
  fichier: string;
  format: string;
  taille: number;
  largeur: number | null;
  hauteur: number | null;
  pages: number | null;
  avertissements: string[];
  date_conversion: string;
  id_norme: number | null;
  perimee: boolean; // l'original a changé depuis la conversion
  conformite: Conformite; // réévaluée contre la règle actuelle
  parametres: ParametresConversion;
  url: string;
}

export interface PieceDossier {
  id: string; // v:photo | v:passeport | d:<nom du fichier>
  origine: OriginePiece;
  nom: string | null;
  cle: string;
  present: boolean;
  regle: ReglePiece | null;
  resume_regle: string;
  format?: string;
  format_libelle?: string;
  extension_trompeuse?: boolean;
  taille?: number;
  mtime?: number;
  largeur?: number | null;
  hauteur?: number | null;
  pages?: number | null;
  erreur?: string | null;
  conformite: Conformite;
  convertible: boolean;
  raison_non_convertible: string | null;
  conversion: ConversionPiece | null;
  url: string | null;
}

export interface DossierNormes {
  dossier: {
    id: number;
    chemin: string;
    libelle_visa: string;
    source: string;
    voyageur: { nom: string; prenom: string };
  };
  norme: NormePj | null;
  origine: OrigineNorme;
  candidates: number[];
  normes_actives: { id: number; libelle: string }[];
  pieces: PieceDossier[];
  outils: EtatOutils;
}

export interface ResultatConvertirTout extends DossierNormes {
  resultats: { piece: string; nom: string | null; ok: boolean; message: string | null }[];
}

// Rectangle normalisé (0..1) de l'outil de recadrage
export interface RectNormalise {
  x: number;
  y: number;
  l: number;
  h: number;
}

// Outil de recadrage (GET /normes-pj/dossiers/:chemin/recadrage)
export interface PreparationRecadrage {
  apercu: string; // data URL JPEG, côté long ≤ 1200 px
  largeur: number; // image d'origine orientée (page d'un PDF : rendue à 200 dpi)
  hauteur: number;
  ratio: number | null; // largeur / hauteur imposé par la règle, null si proportions libres
  suggestion: RectNormalise; // cadre automatique
  pages: number | null; // PDF : nombre de pages
}

// Cadre en pourcentages de l'outil de recadrage (react-image-crop)
export interface CadrePourcentages {
  x: number;
  y: number;
  width: number;
  height: number;
}

// Outils de conversion manquants (état du backend) -> textes de bannière
export const outilsManquants = (o: EtatOutils): string[] => {
  const manques: string[] = [];
  if (!o.sharp) manques.push('sharp (toutes les conversions d\'images) : lancer npm install dans le backend admin');
  if (!o.pdf_lib) manques.push('pdf-lib (création de PDF) : lancer npm install dans le backend admin');
  if (!o.heic_convert) manques.push('heic-convert (photos iPhone) : lancer npm install dans le backend admin');
  if (!o.pdftoppm || !o.pdfinfo) manques.push('poppler (lecture des PDF) : installer poppler-utils sur le serveur, ou définir POPPLER_BIN');
  return manques;
};

export const FORMATS_SORTIE: FormatSortie[] = ['jpg', 'png', 'pdf'];
export const CLES_FIXES: CleFixe[] = ['photo', 'passeport', 'autres'];
export const LIBELLES_CLES_FIXES: Record<CleFixe, string> = { photo: 'Photo', passeport: 'Passeport', autres: 'Autres pièces' };
export const LIMITES = { libelleNorme: 120, libelleRegle: 60, note: 500, regles: 30, tailleKoMax: 51200, pixelsMax: 10000, ratioMax: 1000 };
// Clé de champ du formulaire client (billetAvion, lettreInvitation…)
export const REGEX_CLE_CHAMP = /^[A-Za-z][A-Za-z0-9_]{0,59}$/;

export const estCleFixe = (cle: string): cle is CleFixe => (CLES_FIXES as string[]).includes(cle);
export const estSortieImage = (r: Pick<ReglePiece, 'format_sortie'>): boolean => r.format_sortie === 'jpg' || r.format_sortie === 'png';

// Règle neuve : photo en JPG, passeport et autres pièces en PDF, aucune limite (l'admin les saisit)
export const regleParDefaut = (cle: string): ReglePiece => {
  const format: FormatSortie = cle === 'photo' ? 'jpg' : 'pdf';
  return {
    cle,
    libelle: estCleFixe(cle) ? LIBELLES_CLES_FIXES[cle] : cle,
    formats: [format],
    format_sortie: format,
    taille_min_ko: null,
    taille_max_ko: null,
    largeur_min: null,
    largeur_max: null,
    hauteur_min: null,
    hauteur_max: null,
    proportions: 'libres',
    ratio_l: null,
    ratio_h: null,
  };
};

// Règle prête à envoyer : formats dans l'ordre canonique, dimensions et proportions vidées pour une sortie PDF,
// ratio vidé hors « personnalisées » (le backend refuse ces restes, validation stricte)
export const nettoyerRegle = (r: ReglePiece): ReglePiece => {
  const formats = FORMATS_SORTIE.filter((f) => r.formats.includes(f));
  const format_sortie = formats.includes(r.format_sortie) ? r.format_sortie : (formats[0] || r.format_sortie);
  const image = format_sortie === 'jpg' || format_sortie === 'png';
  const proportions: Proportions = image ? r.proportions : 'libres';
  return {
    ...r,
    libelle: r.libelle.trim(),
    formats,
    format_sortie,
    largeur_min: image ? r.largeur_min : null,
    largeur_max: image ? r.largeur_max : null,
    hauteur_min: image ? r.hauteur_min : null,
    hauteur_max: image ? r.hauteur_max : null,
    proportions,
    ratio_l: proportions === 'personnalisees' ? r.ratio_l : null,
    ratio_h: proportions === 'personnalisees' ? r.ratio_h : null,
  };
};

// ---------------------------------------------------------------------------
// Résumé lisible d'une règle — mêmes textes que resumerRegle (normesPj/schema.js)
//   « JPG · 10 Ko à 1 Mo · 350 à 1000 px · carrée »
// ---------------------------------------------------------------------------
export const formaterKo = (ko: number): string => {
  if (ko < 1024) return `${ko} Ko`;
  return `${String(Math.round((ko / 1024) * 10) / 10).replace('.', ',')} Mo`;
};

// Octets → « 412 Ko », « 1,76 Mo » (mêmes arrondis que formaterTaille de normesPj/conformite.js)
export const formaterTaille = (octets: number): string => {
  const o = Number(octets) || 0;
  if (o >= 1024 * 1024) return `${String(Math.round((o / (1024 * 1024)) * 100) / 100).replace('.', ',')} Mo`;
  return `${Math.max(o > 0 ? 1 : 0, Math.round(o / 1024))} Ko`;
};

const intervalle = (min: number | null, max: number | null, unite: string): string => {
  if (min !== null && max !== null) return min === max ? `${min} ${unite}` : `${min} à ${max} ${unite}`;
  if (min !== null) return `${min} ${unite} min`;
  if (max !== null) return `${max} ${unite} max`;
  return '';
};

export const resumerRegle = (regle: Partial<ReglePiece> | null | undefined): string => {
  const r = regle || {};
  const parts: string[] = [];
  const formats = (r.formats || []).map((f) => f.toUpperCase());
  if (formats.length > 1) parts.push(`${formats.join(' ou ')} (conversion en ${String(r.format_sortie || '').toUpperCase()})`);
  else if (formats.length) parts.push(formats[0]);

  const min = r.taille_min_ko === undefined ? null : r.taille_min_ko;
  const max = r.taille_max_ko === undefined ? null : r.taille_max_ko;
  if (min !== null && max !== null) parts.push(`${formaterKo(min)} à ${formaterKo(max)}`);
  else if (max !== null) parts.push(`${formaterKo(max)} max`);
  else if (min !== null) parts.push(`${formaterKo(min)} min`);

  const v = (k: 'largeur_min' | 'largeur_max' | 'hauteur_min' | 'hauteur_max') => (r[k] === undefined ? null : r[k] as number | null);
  const l = intervalle(v('largeur_min'), v('largeur_max'), 'px');
  const h = intervalle(v('hauteur_min'), v('hauteur_max'), 'px');
  if (l && l === h) parts.push(l);
  else {
    if (l) parts.push(`largeur ${l}`);
    if (h) parts.push(`hauteur ${h}`);
  }

  if (r.proportions === 'carree') parts.push('carrée');
  else if (r.proportions === 'personnalisees' && r.ratio_l && r.ratio_h) parts.push(`${r.ratio_l}:${r.ratio_h}`);
  return parts.join(' · ');
};

// ---------------------------------------------------------------------------
// Validation d'une règle avant envoi (retour immédiat dans l'éditeur ; le backend revalide et reste l'autorité)
// ---------------------------------------------------------------------------
const entierDans = (v: number | null, min: number, max: number): boolean => v !== null && Number.isInteger(v) && v >= min && v <= max;

export const validerRegle = (regle: ReglePiece): string[] => {
  const r = nettoyerRegle(regle);
  const erreurs: string[] = [];
  if (!r.cle) erreurs.push('Clé obligatoire.');
  else if (!estCleFixe(r.cle) && !REGEX_CLE_CHAMP.test(r.cle)) erreurs.push(`Clé « ${r.cle} » invalide : lettres, chiffres et _, commence par une lettre.`);
  if (r.libelle.length > LIMITES.libelleRegle) erreurs.push(`Libellé : ${LIMITES.libelleRegle} caractères max.`);
  else if (/[<>]/.test(r.libelle)) erreurs.push('Libellé : les caractères < et > sont interdits.');
  if (!r.formats.length) erreurs.push('Au moins un format accepté.');

  ([['Poids min', r.taille_min_ko], ['Poids max', r.taille_max_ko]] as [string, number | null][]).forEach(([nom, v]) => {
    if (v !== null && !entierDans(v, 1, LIMITES.tailleKoMax)) erreurs.push(`${nom} : entier de 1 à ${LIMITES.tailleKoMax} Ko.`);
  });
  if (entierDans(r.taille_min_ko, 1, LIMITES.tailleKoMax) && entierDans(r.taille_max_ko, 1, LIMITES.tailleKoMax)
    && (r.taille_min_ko as number) >= (r.taille_max_ko as number)) erreurs.push('Poids min : doit être inférieur au poids max.');

  if (estSortieImage(r)) {
    const dims: [string, number | null][] = [
      ['Largeur min', r.largeur_min], ['Largeur max', r.largeur_max], ['Hauteur min', r.hauteur_min], ['Hauteur max', r.hauteur_max],
    ];
    dims.forEach(([nom, v]) => {
      if (v !== null && !entierDans(v, 1, LIMITES.pixelsMax)) erreurs.push(`${nom} : entier de 1 à ${LIMITES.pixelsMax} px.`);
    });
    const valides = dims.every(([, v]) => v === null || entierDans(v, 1, LIMITES.pixelsMax));
    if (valides && r.largeur_min !== null && r.largeur_max !== null && r.largeur_min > r.largeur_max) erreurs.push('Largeur min : doit être inférieure ou égale à la largeur max.');
    if (valides && r.hauteur_min !== null && r.hauteur_max !== null && r.hauteur_min > r.hauteur_max) erreurs.push('Hauteur min : doit être inférieure ou égale à la hauteur max.');
    if (r.proportions === 'personnalisees' && (!entierDans(r.ratio_l, 1, LIMITES.ratioMax) || !entierDans(r.ratio_h, 1, LIMITES.ratioMax))) {
      erreurs.push(`Proportions : deux entiers de 1 à ${LIMITES.ratioMax} (ex. 35 et 45).`);
    }
    // Existe-t-il une taille respectant à la fois largeur, hauteur et proportions ?
    const ratio = r.proportions === 'carree' ? 1
      : (r.proportions === 'personnalisees' && r.ratio_l && r.ratio_h ? r.ratio_l / r.ratio_h : null);
    if (valides && ratio !== null && erreurs.length === 0) {
      const lMin = r.largeur_min === null ? 1 : r.largeur_min;
      const lMax = r.largeur_max === null ? LIMITES.pixelsMax : r.largeur_max;
      const hMin = r.hauteur_min === null ? 1 : r.hauteur_min;
      const hMax = r.hauteur_max === null ? LIMITES.pixelsMax : r.hauteur_max;
      if (Math.max(lMin, hMin * ratio) > Math.min(lMax, hMax * ratio) + 1e-9) {
        erreurs.push('Aucune taille ne respecte à la fois la largeur, la hauteur et les proportions.');
      }
    }
  }
  return erreurs;
};

// Message d'une erreur axios (400 : { message, erreurs: [] } ; 409 : { message, conflits } ; 503 : { message })
export const messageErreur = (e: unknown, defaut: string): string => {
  if (axios.isAxiosError(e) && e.response && e.response.data) {
    const data = e.response.data as { message?: string; erreurs?: string[] };
    if (Array.isArray(data.erreurs) && data.erreurs.length) return data.erreurs.join(' ');
    if (data.message) return data.message;
  }
  return defaut;
};

// ---------------------------------------------------------------------------
// Recadrage manuel (fenêtre « Recadrer »)
// ---------------------------------------------------------------------------
// Côté long sous lequel une image mise en PDF devient illisible (COTE_MIN_IMAGE_PDF de normesPj/conversion.js)
export const COTE_MIN_LISIBLE = 800;

type ProportionsRegle = Pick<ReglePiece, 'proportions' | 'ratio_l' | 'ratio_h'>;

// Proportions imposées par une règle (largeur / hauteur), null si libres (ratioRegle de normesPj/conversion.js)
export const ratioRegle = (r: ProportionsRegle | null): number | null => {
  if (!r) return null;
  if (r.proportions === 'carree') return 1;
  if (r.proportions === 'personnalisees' && r.ratio_l && r.ratio_h) return r.ratio_l / r.ratio_h;
  return null;
};

// « carrée », « 3:4 », '' si libres
export const libelleProportions = (r: ProportionsRegle | null): string => {
  if (!r) return '';
  if (r.proportions === 'carree') return 'carrée';
  if (r.proportions === 'personnalisees' && r.ratio_l && r.ratio_h) return `${r.ratio_l}:${r.ratio_h}`;
  return '';
};

// Recadrage possible : pièce convertible avec une règle, sauf un PDF converti en PDF (pages recompressées telles quelles)
export const peutRecadrer = (p: PieceDossier): boolean =>
  p.present && !!p.regle && p.convertible && !(p.format === 'pdf' && p.regle.format_sortie === 'pdf');

const arrondi4 = (v: number): number => Math.round(v * 10000) / 10000;
const borne = (v: number, min: number, max: number): number => Math.min(max, Math.max(min, v));

// Cadre de l'outil (pourcentages) -> rectangle normalisé envoyé au backend : borné à l'image, 4 décimales
export const versRect = (c: CadrePourcentages): RectNormalise => {
  const x = borne(arrondi4(c.x / 100), 0, 1);
  const y = borne(arrondi4(c.y / 100), 0, 1);
  return { x, y, l: borne(arrondi4(c.width / 100), 0, arrondi4(1 - x)), h: borne(arrondi4(c.height / 100), 0, arrondi4(1 - y)) };
};

export const versPourcentages = (r: RectNormalise): CadrePourcentages => ({ x: r.x * 100, y: r.y * 100, width: r.l * 100, height: r.h * 100 });

// Taille en pixels de l'image d'origine d'un rectangle normalisé
export const pixelsDuCadre = (r: RectNormalise, img: Pick<PreparationRecadrage, 'largeur' | 'hauteur'>): { l: number; h: number } =>
  ({ l: Math.max(1, Math.round(r.l * img.largeur)), h: Math.max(1, Math.round(r.h * img.hauteur)) });

// Cadre ajusté à la main de la version conforme, repris tant que l'original n'a pas changé et pour la même page
// (« Reconvertir » et ouverture de l'outil) ; null = cadre automatique
export const cadreRepris = (c: ConversionPiece | null, page: number | null): RectNormalise | null => {
  if (!c || c.perimee || !c.parametres || !c.parametres.recadrage) return null;
  return (c.parametres.page || 1) === (page || 1) ? c.parametres.recadrage : null;
};

// Cadre à l'ouverture de l'outil : cadre repris s'il respecte encore les proportions de la règle, sinon cadre automatique
export const cadreInitial = (prep: PreparationRecadrage, c: ConversionPiece | null, page: number | null): RectNormalise => {
  const repris = cadreRepris(c, page);
  if (!repris) return prep.suggestion;
  if (prep.ratio !== null) {
    const px = pixelsDuCadre(repris, prep);
    if (Math.abs(px.l / px.h - prep.ratio) > 0.01 * prep.ratio) return prep.suggestion;
  }
  return repris;
};

// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------
export const etatNormesPj = async (): Promise<EtatNormesPj> =>
  (await axios.get<EtatNormesPj>(`${BASE_URL}/normes-pj/etat`)).data;

export const listerNormesPj = async (): Promise<NormePj[]> =>
  (await axios.get<NormePj[]>(`${BASE_URL}/normes-pj`)).data;

export const creerNormePj = async (d: NormePjInput): Promise<NormePj> =>
  (await axios.post<NormePj>(`${BASE_URL}/normes-pj`, d)).data;

export const majNormePj = async (id: number, d: Partial<NormePjInput>): Promise<NormePj> =>
  (await axios.put<NormePj>(`${BASE_URL}/normes-pj/${id}`, d)).data;

export const supprimerNormePj = async (id: number): Promise<void> => {
  await axios.delete(`${BASE_URL}/normes-pj/${id}`);
};

// Types de pièces (clés de champ) vus dans les dossiers récents correspondant au ciblage
export const champsObserves = async (
  d: Pick<NormePjInput, 'visas_titres' | 'motif_tous' | 'motif_un_parmi'>
): Promise<{ dossiers: number; champs: ChampObserve[] }> =>
  (await axios.post<{ dossiers: number; champs: ChampObserve[] }>(`${BASE_URL}/normes-pj/champs-observes`, d)).data;

// --- Fenêtre « Normes PJ » d'une demande ---
const urlDossier = (chemin: string): string => `${BASE_URL}/normes-pj/dossiers/${encodeURIComponent(chemin)}`;

const requete = (params: Record<string, string | number | null | undefined>): string => {
  const q = Object.keys(params)
    .filter((k) => params[k] !== undefined && params[k] !== null && params[k] !== '')
    .map((k) => `${k}=${encodeURIComponent(String(params[k]))}`)
    .join('&');
  return q ? `?${q}` : '';
};

// norme : id imposé dans la fenêtre (null = choix automatique)
export const chargerDossierNormes = async (chemin: string, norme: number | null): Promise<DossierNormes> =>
  (await axios.get<DossierNormes>(urlDossier(chemin) + requete({ norme }))).data;

export const convertirPieceDossier = async (
  chemin: string,
  corps: { piece: string; norme: number | null; page?: number | null; recadrage?: RectNormalise | null }
): Promise<{ piece: PieceDossier; copie: boolean }> =>
  (await axios.post<{ piece: PieceDossier; copie: boolean }>(`${urlDossier(chemin)}/convertir`, corps)).data;

export const convertirToutDossier = async (chemin: string, norme: number | null): Promise<ResultatConvertirTout> =>
  (await axios.post<ResultatConvertirTout>(`${urlDossier(chemin)}/convertir-tout`, { norme })).data;

// Aperçu et cadre automatique de l'outil de recadrage ; page : PDF converti en image (null = 1)
export const preparerRecadrage = async (chemin: string, piece: string, norme: number | null, page: number | null): Promise<PreparationRecadrage> =>
  (await axios.get<PreparationRecadrage>(`${urlDossier(chemin)}/recadrage${requete({ piece, page, norme })}`)).data;

// Vignette JPEG (balise <img>) ; v : valeur qui change quand le fichier change (cache du navigateur)
export const urlApercu = (
  chemin: string, piece: string, version: 'original' | 'conforme', v: string | number | undefined, page?: number | null
): string => `${urlDossier(chemin)}/apercu${requete({ piece, version, page, l: 240, v })}`;

// Téléchargement sous un nom prêt à déposer (Content-Disposition: attachment)
export const urlTelechargement = (chemin: string, piece: string, version: 'original' | 'conforme', norme: number | null): string =>
  `${urlDossier(chemin)}/telecharger${requete({ piece, version, norme })}`;
